/**
 * Chi decodifica, e come ci si accorge che non sta decodificando niente.
 *
 * ## Le due domande, e perché stanno insieme
 *
 * «Quale strada prende questo apparecchio» e «questa strada sta funzionando» sono la stessa
 * conversazione: la seconda è l'unica che permette di cambiare la prima. Il 04-10-2026 un canale su
 * iPhone dava **audio e schermo nero**, e l'app non lo sapeva — nessun errore, nessun evento, niente
 * da mostrare e niente da scrivere in una segnalazione. Quel che la persona impara è «non funziona».
 *
 * ## Il nero con l'audio non è un guasto generico
 *
 * È una diagnosi precisa: il flusso arriva, la linea è autorizzata, il contenitore si apre, l'audio
 * si decodifica — e il decoder **video** rifiuta la sua traccia senza dirlo. Succede, fra l'altro,
 * con l'H.265 dentro MPEG-TS, che Apple non accetta su nessun modello mentre Android lo riproduce
 * senza fare storie.
 *
 * Si riconosce da un fatto misurabile sull'elemento: **l'orologio avanza e i fotogrammi no.**
 */

/** Cosa sa fare questo apparecchio. Si legge una volta, all'avvio, da chi ha accesso ai globali. */
export interface PlaybackCapabilities {
  /** `ManagedMediaSource`: Apple l'ha aperta in iOS 17.1, ed è ciò che permette a hls.js di girare lì. */
  readonly managedMediaSource: boolean;
  /** `MediaSource` vera e propria: i computer e Android. */
  readonly mediaSource: boolean;
  /** `VideoDecoder` di WebCodecs: il decoder **hardware** comandato da noi. Safari da iOS 17. */
  readonly webCodecs: boolean;
  /** Il lettore apre un `.m3u8` da sé: Safari, e qualche WebView di televisore. */
  readonly nativeHls: boolean;
}

/**
 * Chi porta i fotogrammi sullo schermo.
 *
 * `hls-mms` e `hls-mse` sono la stessa libreria su due attacchi diversi, e la distinzione non è
 * pedanteria: su `ManagedMediaSource` il sistema può **sospendere** la sorgente quando vuole, e un
 * difetto che si vede solo lì non si riproduce da nessun'altra parte.
 */
export type PlaybackPipeline =
  | 'hls-mms'
  | 'hls-mse'
  | 'native-hls'
  | 'webcodecs'
  | 'file'
  | 'none';

/**
 * Quale strada per questo contenuto, su questo apparecchio.
 *
 * Un file on demand non passa da nessuna libreria di streaming: è un file, e l'elemento lo apre da
 * sé — è anche l'unico modo di avere lo scorrimento libero dentro.
 *
 * Per la diretta l'ordine è: la sorgente gestita **prima** di quella classica dove c'è, perché dove
 * c'è è l'unica che il sistema accetti; poi l'HLS nativo; e in fondo WebCodecs, che è l'unico che
 * resta quando il lettore di sistema non vuole saperne.
 *
 * WebCodecs **non** è primo apposta. Decodificare noi significa anche dipingere noi, sincronizzare
 * noi l'audio e rifare noi i sottotitoli: dove il lettore di sistema fa il suo mestiere, toglierglielo
 * è lavoro in più e superficie in più per sbagliare.
 */
export function choosePipeline(
  capabilities: PlaybackCapabilities,
  kind: 'hls' | 'file',
): PlaybackPipeline {
  if (kind === 'file') return 'file';
  if (capabilities.managedMediaSource) return 'hls-mms';
  if (capabilities.mediaSource) return 'hls-mse';
  if (capabilities.nativeHls) return 'native-hls';
  if (capabilities.webCodecs) return 'webcodecs';
  return 'none';
}

/**
 * Per quanto si guarda un orologio che avanza senza fotogrammi, prima di dire che il video non c'è.
 *
 * Tre secondi: il primo fotogramma di un canale può tardare, e dichiarare al primo controllo
 * vorrebbe dire scambiare un avvio lento per un decoder che rifiuta. Oltre, l'audio che va avanti da
 * solo non è più un'attesa.
 */
export const BLANK_VIDEO_GRACE_MS = 3_000;

/** Quel che si legge sull'elemento per sapere se sta davvero mostrando qualcosa. */
export interface VideoVitals {
  /** `videoWidth`: zero finché non è stato decodificato **nessun** fotogramma. */
  readonly videoWidth: number;
  /** `getVideoPlaybackQuality().totalVideoFrames`, o `null` dove il lettore non lo espone. */
  readonly totalVideoFrames: number | null;
  /** Da quanto l'orologio del contenuto sta avanzando, in millisecondi. */
  readonly playingForMs: number;
}

/**
 * Vero quando l'apparecchio sta riproducendo **l'audio e basta**.
 *
 * La misura è `videoWidth === 0`: finché non viene decodificato un fotogramma quella resta a zero,
 * ed è il segnale che il decoder video non ha mai accettato la traccia. Dove il lettore espone anche
 * il conto dei fotogrammi si guarda pure quello — un `videoWidth` valorizzato con zero fotogrammi
 * decodificati è lo stesso guasto, visto da un altro lato.
 *
 * `playingForMs` e non «numero di controlli»: su questa piattaforma è già costato una volta —
 * «riprovo dieci volte» è un tempo che dipende da quanto è lenta la rete, cioè non è un tempo.
 */
export function videoIsBlank(vitals: VideoVitals): boolean {
  if (vitals.playingForMs < BLANK_VIDEO_GRACE_MS) return false;
  if (vitals.videoWidth === 0) return true;
  return vitals.totalVideoFrames === 0;
}

/**
 * Cosa fare quando si scopre che il video non c'è, su questo apparecchio.
 *
 * `retry` non compare apposta: rifare la stessa strada con lo stesso flusso dà lo stesso nero, e
 * ripetere un tentativo che non può riuscire è il modo in cui ci si fa bandire l'indirizzo dal
 * gateway — 3000 richieste ogni quarto d'ora, già pagate il 27-09-2026.
 */
export type BlankVideoRemedy =
  /** C'è WebCodecs: si decodifica noi, in hardware, invece di chiedere al lettore di sistema. */
  | { readonly action: 'switchTo'; readonly pipeline: 'webcodecs' }
  /** Non c'è niente da provare: si dice perché, e si dice con il suo nome. */
  | { readonly action: 'report'; readonly code: 'stream.video_not_decoded' };

export function blankVideoRemedy(
  capabilities: PlaybackCapabilities,
  current: PlaybackPipeline,
): BlankVideoRemedy {
  // Già su WebCodecs e ancora nero: il decoder è quello hardware, il contenitore l'abbiamo smontato
  // noi, e non resta nessuna strada **su questo apparecchio**. Dirlo è l'unica cosa onesta.
  if (capabilities.webCodecs && current !== 'webcodecs') {
    return { action: 'switchTo', pipeline: 'webcodecs' };
  }
  return { action: 'report', code: 'stream.video_not_decoded' };
}
